import { AutogenLayoutCommissionsGroupByTable } from "./autogen/AutogenLayoutCommissionsGroupByTable";
import { Currency, ModelRow } from "@mcleod/core";
import {
    ClickEvent, Image, Layout, Tab, Table, TableRow, TableRowDisplayEvent, TableSelectionEvent, Tabset, Textbox
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { CommissionsProcessingTabLayout } from "./CommissionsProcessing";
import { CommissionsProcessingUtil } from "./CommissionsProcessingUtil";

export class CommissionsGroupByTable extends AutogenLayoutCommissionsGroupByTable {
    private tabsetCommissionsProcessing: Tabset;

    onLoad(): void | Promise<void> {
        this.buttonClearSelections.color = "primary.light";
        this.tableGroupBy.addSelectionListener((event: TableSelectionEvent) => {
            this.refreshCustomFilter();
            this.updateRowsSelectedButton();
            this.buttonClearSelections.enabled = this.tableGroupBy.selectedRows.length > 0;
            this.buttonClearSelections.color = this.buttonClearSelections.enabled ? "primary" : "primary.light";
            this.selectFirstNonZeroTab();
        });
    }

    getGroupByCustomFilter(layout: CommissionsProcessingTabLayout): (row: TableRow) => boolean {
        const activeTaskFilter = layout.filterPanel.getActiveFilter();
        const baseLayoutFilter = layout.getLayoutBaseFilter;

        return (row: TableRow) => {
            const taskFilterMatches: boolean = activeTaskFilter == null || activeTaskFilter.filter(row.data);
            const selectedRows = this.tableGroupBy.selectedRows;
            const groupByMatches: boolean = selectedRows.length == 0 || selectedRows.some((tableRow: TableRow) => tableRow.data.get("payee_id") == row.data.get("payee_id"));
            return baseLayoutFilter(row.data) && groupByMatches && taskFilterMatches;
        }
    }

    updateRowsSelectedButton() {
        this.buttonClearSelections.caption = `Clear ${this.tableGroupBy.selectedRows.length.toString()} ${this.tableGroupBy.selectedRows.length == 1 ? "Selection" : "Selections"}`;
        this.buttonClearSelections.tooltip = this.generateRowsSelectedTooltip();
    }

    generateRowsSelectedTooltip(): string {
        const groupByValues = this.tableGroupBy.selectedRows.map(row => row.data.get("payee_id"));
        if (groupByValues.length == 0) {
            return null;
        }
        groupByValues.sort();
        return groupByValues.map(value => "• " + value).join("\n");
    }

    buttonClearSelectionsOnClick(event: ClickEvent) {
        this.tableGroupBy.selectedRows = []
        this.refreshCustomFilter();
    }

    refreshCustomFilter() {
        this.tabsetCommissionsProcessing.forEveryChildComponent((component) => {
            if (component instanceof Layout && CommissionsProcessingUtil.isCommissionsProcessingLayoutTab(component)) {
                const layout: CommissionsProcessingTabLayout = component as CommissionsProcessingTabLayout;
                layout.filterPanel.applyRemoveFilter = (filter) => {
                    layout.tableCommissionsProcessing.customFilter = this.getGroupByCustomFilter(layout);
                }
                layout.filterPanel.updateFilterCounts = async () => {
                    if (layout.filterPanel.filters.length == 0) return;

                    // reset counts
                    for (const filter of layout.filterPanel.filters) {
                        filter.count = 0;
                        filter.button.caption = `${filter.caption} ${filter.count}`;
                    }
                    const tableCommissionsProcessing = layout.tableCommissionsProcessing;
                    for (const tableRow of tableCommissionsProcessing.allRows) {
                        const modelRow = tableRow.data as ModelRow;
                        for (const filter of layout.filterPanel.filters) {
                            if (filter.filter(modelRow) && layout.getLayoutBaseFilter(modelRow) && tableCommissionsProcessing.customFilter(tableRow)) {
                                filter.count++;
                                filter.button.caption = `${filter.caption} ${filter.count}`;
                            }
                        }
                    }
                }
                layout.tableCommissionsProcessing.customFilter = this.getGroupByCustomFilter(layout);
                layout.filterPanel.updateFilterCounts();
            }
        });
        this.setTabTitles();
    }

    public initialize(tabset: Tabset): void {
        this.tabsetCommissionsProcessing = tabset;
        this.refreshCustomFilter();
        this.groupByField();
    }

    async groupByField() {
        if (this.tableGroupBy.selectedRows.length > 0) {
            this.tableGroupBy.selectedRows = [];
        }
        const groupByMap: Map<string, {
            payee_id: ModelRow[]
            count: number;
            hold_comm_margin: Currency;
            process_comm_margin: Currency;
            has_overridden_rate: boolean;
        }> = new Map();

        for (const row of this.mainDataSource.data) {
            const groupByValue = row.get("payee_id");//?.get("name");
            if (groupByValue == null) continue;
            if (!groupByMap.has(groupByValue)) {
                groupByMap.set(groupByValue, {
                    count: 0,
                    hold_comm_margin: new Currency(0),
                    process_comm_margin: new Currency(0),
                    payee_id: row.getLookupModelData("payee_id"),
                    has_overridden_rate: false
                });
            }
            groupByMap.get(groupByValue).count++;
            if (row.get("ready_to_pay_flag") !== "V") {
                const commissionableMargin = row.isNull("commissions_split_percent") ? row.getCurrency("commissionable_margin") : row.getCurrency("split_comm_margin");
                if (row.get("ready_to_pay_flag") === "Y") {
                    await groupByMap.get(groupByValue).process_comm_margin.add(commissionableMargin);
                } else {
                    await groupByMap.get(groupByValue).hold_comm_margin.add(commissionableMargin);
                }
            }
            if (row.getBoolean("override_rate", false)) {
                groupByMap.get(groupByValue).has_overridden_rate = true;
            }
        }

        const rows = [];
        groupByMap.forEach((value, key) => {
            const row = new ModelRow("", false);
            row.set("count", value.count);
            row.set("process_comm_margin", value.process_comm_margin);
            row.set("hold_comm_margin", value.hold_comm_margin);
            row.set("payee_id", key)
            row.setLookupModelData("payee_id", value.payee_id)
            row.set("has_overridden_rate", value.has_overridden_rate);

            rows.push(row);
        });
        this.tableGroupBy.displayComponentData(null, rows, 0);
        this.tableGroupBy.applySort("payee_id", "asc");
        const tabMap = new Map<string, Table>
        this.tabsetCommissionsProcessing.forEveryChildComponent((component) => {
            if (component instanceof Layout && CommissionsProcessingUtil.isCommissionsProcessingLayoutTab(component)) {
                const table = (component as CommissionsProcessingTabLayout).tableCommissionsProcessing;
                tabMap.set(component.id, table);
            }
        });
        tabMap.forEach((table, key) => {
            table.resetColumnSorting().then(() => {
                table.applySort("payee_id", "asc");
                table.applySort("ok2pay_date", "asc", true);
                table.applySort("order_id", "asc", true);
            });
        });
    }

    tableGroupByOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const textboxGroupByValue = tableRow.findComponentById("textboxPayee") as Textbox;
        CommonTooltips.setTooltipContentLayout(textboxGroupByValue, null, "lme/settlement/officepayroll/OfficePayeeQuickInfo", null, { id: modelRow.get("payee_id") });
        const imageOverriddenRate = tableRow.findComponentById("imageOverriddenRate") as Image;
        if (modelRow.getBoolean("has_overridden_rate", false) && imageOverriddenRate) {
            imageOverriddenRate.visible = true;
        }
    }

    setTabTitles(): void {
        const tabMap = new Map<string, {
            count: number,
            getLayoutBaseFilter: (row: ModelRow) => boolean
        }>();
        this.tabsetCommissionsProcessing.forEveryChildComponent(async (component) => {
            if (component instanceof Tab) {
                const tab = component as Tab;
                if (tab.id == "tabProcess" || tab.id == "tabVoid" || tab.id == "tabHold") {
                    const layout = tab.findComponentByType(Layout) as CommissionsProcessingTabLayout;
                    tabMap.set(tab.id, {
                        count: 0,
                        getLayoutBaseFilter: layout.getLayoutBaseFilter
                    });
                }
            }
        });
        for (const row of this.mainDataSource.data) {
            for (const [key, value] of tabMap) {
                if (value.getLayoutBaseFilter(row) && (this.tableGroupBy.selectedRows.length == 0 || this.tableGroupBy.selectedRows.find(selectedRow => selectedRow.data.get("payee_id") == row.get("payee_id")))) {
                    value.count++;
                }
            }
        }
        tabMap.forEach((value, key) => {
            const tab = this.tabsetCommissionsProcessing.findComponentById(key) as Tab;
            const count = value.count;
            // first remove the number from the caption
            tab.caption = tab.caption.replace(/ \d+$/, "");
            tab.caption = `${tab.caption} ${count}`;
        });
        this.tabsetCommissionsProcessing.refreshUnderline();
    }

    selectFirstNonZeroTab() {
        const currentTab = this.tabsetCommissionsProcessing.getTab(this.tabsetCommissionsProcessing.selectedIndex);
        const tabMap = new Map<string, Table>
        this.tabsetCommissionsProcessing.forEveryChildComponent((component) => {
            if (component instanceof Layout && CommissionsProcessingUtil.isCommissionsProcessingLayoutTab(component)) {
                const layout = component as CommissionsProcessingTabLayout;
                const table = layout.tableCommissionsProcessing;
                tabMap.set(component.parent.id, table);
            }
        });
        if (tabMap.get(currentTab.id).filteredRows.length > 0) {
            return;
        }
        this.tabsetCommissionsProcessing.tabs.some((tab, index) => {
            if (tab.id != currentTab.id && tabMap.get(tab.id).filteredRows.length > 0) {
                tab.select();
                return true;
            }
        });
    }
}
